import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { View, TextInput, Animated, StyleSheet, TextInputProps } from 'react-native';
import themes from '_/constants/themes';

interface FloatingInputProps extends TextInputProps {
  label?: string;
  rightContent?: React.ReactNode;
  leftContent?: React.ReactNode;
  error?: boolean;
  success?: boolean;
}

const FloatingInput = forwardRef<TextInput, FloatingInputProps>(
  ({ label, value, onChangeText, rightContent, leftContent, error, success, ...rest }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const animated = useRef(new Animated.Value(value ? 1 : 0)).current;

    const getBorderColor = () => {
      if (error) return themes.colors.danger;
      if (success) return themes.colors.success;
      if (isFocused) return themes.colors.primary;

      return themes.colors.lightTertiary;
    };

    useEffect(() => {
      Animated.timing(animated, {
        toValue: isFocused || value ? 1 : 0,
        duration: 150,
        useNativeDriver: false,
      }).start();
    }, [isFocused, value]);

    const labelStyle = {
      position: 'absolute' as const,
      left: 16,
      top: animated.interpolate({
        inputRange: [0, 1],
        outputRange: [18, -8],
      }),
      fontSize: animated.interpolate({
        inputRange: [0, 1],
        outputRange: [16, 12],
      }),
      color: animated.interpolate({
        inputRange: [0, 1],
        outputRange: [
          error ? themes.colors.danger : themes.colors.grayPrimary,
          error ? themes.colors.danger : success ? themes.colors.success : themes.colors.primary,
        ],
      }),
      backgroundColor: themes.colors.white,
      paddingHorizontal: 4,
      zIndex: 1,
      borderRadius: 4,
    };

    return (
      <View style={styles.container}>
        {label && <Animated.Text style={labelStyle}>{label}</Animated.Text>}

        <View style={[styles.inputWrapper, { borderColor: getBorderColor() }]}>
          {leftContent && <View style={styles.leftContent}>{leftContent}</View>}

          <TextInput
            {...rest}
            ref={ref}
            value={value}
            onChangeText={onChangeText}
            style={styles.input}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />

          {rightContent && <View style={styles.rightContent}>{rightContent}</View>}
        </View>
      </View>
    );
  }
);

export default FloatingInput;

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  inputWrapper: {
    borderWidth: 1.5,
    borderRadius: 14,
    backgroundColor: themes.colors.white,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  input: {
    flex: 1,
    paddingVertical: 18,
    fontSize: 16,
  },
  rightContent: {
    marginLeft: 8,
  },
  leftContent: {
    marginRight: 8,
  },
});
